/**
 * admin/src/pages/UserDetailPage.tsx
 * Chi tiết 1 user: thông tin cơ bản + học sinh + subscriptions
 */

import { useParams, useNavigate, Link } from 'react-router-dom';
import { useUser } from '../hooks/useUsers';
import {
  TierBadge, Badge, LoadingSpinner, ErrorBanner,
  formatDate, timeAgo
} from '../components/ui/index';

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isLoading, error, refetch } = useUser(id);

  if (isLoading) {
    return (
      <div className="flex-center" style={{ height: 300 }}>
        <LoadingSpinner size={36} />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>
          ← Quay lại
        </button>
        <ErrorBanner message={error ?? 'Không tìm thấy user'} onRetry={refetch} />
      </div>
    );
  }

  const activeSubs = user.subscriptions?.filter(s => s.is_active) ?? [];

  return (
    <div>
      {/* ── Header ── */}
      <div className="page-header">
        <div className="page-header__left">
          <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)} style={{ marginBottom: 8 }}>
            ← Quay lại
          </button>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {user.name ?? '(Chưa đặt tên)'}
            <TierBadge tier={user.subscription_tier} />
          </h1>
          <p>{user.email}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link
            to={`/admin/subscriptions`}
            className="btn btn-secondary"
          >
            💳 Cấp gói
          </Link>
        </div>
      </div>

      {/* ── Grid: Info + Subs ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>

        {/* Thông tin cơ bản */}
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>Thông tin tài khoản</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Row label="ID" value={<code>{user.id}</code>} />
            <Row label="Email" value={user.email} />
            <Row label="Tên" value={user.name ?? '—'} />
            <Row label="Email thông báo" value={user.notification_email ?? '—'} />
            <Row label="Tier" value={<TierBadge tier={user.subscription_tier} />} />
            <Row label="Ngày đăng ký" value={formatDate(user.created_at)} />
            <Row label="Số học sinh" value={String(user.student_count)} />
          </div>
        </div>

        {/* Subscriptions */}
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>
            Subscriptions
            <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 8 }}>
              ({user.subscriptions?.length ?? 0} tổng, {activeSubs.length} active)
            </span>
          </h3>
          {(!user.subscriptions || user.subscriptions.length === 0) ? (
            <div className="empty-state" style={{ padding: 24 }}>
              <div className="empty-state__icon">📭</div>
              <div className="empty-state__text">Chưa có subscription nào</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {user.subscriptions.slice(0, 6).map(sub => (
                <div key={sub.id} style={{
                  padding: '10px 14px',
                  background: 'var(--bg-elevated)',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <TierBadge tier={sub.plan} />
                      {sub.is_active ? (
                        <Badge variant="success">✅ Active</Badge>
                      ) : (
                        <Badge variant="danger">❌ Hết hạn</Badge>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>
                      Hết hạn: {formatDate(sub.expires_at)}
                      {sub.payment_ref && <> · {sub.payment_ref}</>}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', textAlign: 'right' }}>
                    {formatDate(sub.created_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Học sinh ── */}
      <div className="card">
        <h3 style={{ marginBottom: 16 }}>
          Học sinh ({user.students?.length ?? 0})
        </h3>
        {(!user.students || user.students.length === 0) ? (
          <div className="empty-state" style={{ padding: 32 }}>
            <div className="empty-state__icon">🧒</div>
            <div className="empty-state__text">Chưa tạo học sinh nào</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            {user.students.map(student => (
              <Link
                key={student.id}
                to={`/admin/students/${student.id}`}
                style={{ textDecoration: 'none' }}
              >
                <div style={{
                  padding: 16,
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  transition: 'border-color 0.15s',
                  cursor: 'pointer',
                }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--accent)')}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <span style={{ fontSize: 28 }}>{student.avatar_id || '🧒'}</span>
                    <div>
                      <div className="cell-primary">{student.display_name}</div>
                      <div className="cell-muted">{student.theme}</div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, textAlign: 'center' }}>
                    <StatMini label="⭐ Sao" value={student.total_stars} />
                    <StatMini label="🔥 Streak" value={`${student.current_streak}d`} />
                    <StatMini label="📝 Sessions" value={student.total_sessions} />
                  </div>
                  {student.last_active && (
                    <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 8, textAlign: 'right' }}>
                      {timeAgo(student.last_active)}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ width: 140, fontSize: 12, color: 'var(--text-3)', flexShrink: 0 }}>{label}</span>
      <span style={{ color: 'var(--text-1)', fontSize: 13 }}>{value}</span>
    </div>
  );
}

function StatMini({ label, value }: { label: string; value: string | number }) {
  return (
    <div style={{
      background: 'var(--bg-base)',
      borderRadius: 6,
      padding: '6px 4px',
    }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)' }}>{value}</div>
      <div style={{ fontSize: 10, color: 'var(--text-3)' }}>{label}</div>
    </div>
  );
}
